import { getDeviceId } from '@doneverse/data/api'
import type {
  ActiveCall,
  CallParticipantSummary,
  CallRealtimeEvent,
  CallRingEntry,
  CallRingState,
} from '@doneverse/shared'
import type { StateCreator } from 'zustand'
import type { DashboardStore, PersistMutator } from './index'

/** The call this user is currently IN. */
export interface ActiveCallSession {
  callId: string
  /** This user's contact_id for the conversation (matches `openChatContactId`). */
  contactId: string
  contactName: string
  meetingId: string
  /**
   * Short-lived RealtimeKit participant token. Deliberately NOT persisted: it's
   * a credential, and a call can't survive a page reload anyway (the media
   * connection dies with the tab).
   */
  authToken: string
  participantId: string
  minimized: boolean
  /**
   * Set when this device must give the call up: hang the media up, drop the
   * dock, and do NOT report a leave (the seat belongs to another device, or the
   * call is already over). Only CallHost holds the handle to `meeting.leave()`,
   * so it does the teardown — this is how it finds out.
   *
   * Lives on the session rather than beside it so "releasing a call we aren't
   * in" is unrepresentable.
   */
  releasing: CallReleaseReason | null
  /**
   * When this device took the seat (epoch ms).
   *
   * Guards the self-healing poll against its own cache: the active-call query
   * is shared with the open thread, which has usually already cached
   * `{call: null}` from before the call existed. Acting on a read older than
   * the join would tear the dock down the instant it appeared.
   */
  joinedAt: number
  /**
   * Live ring roster for the call we're in, kept fresh by `call.ring_changed`.
   * The caller's half of the ring state machine: it's what lets the dock say
   * "Ringing Dan…" and then "Declined" instead of sitting there silently
   * looking identical to a call in progress.
   */
  ring: CallRingEntry[]
}

/** A call running in one of this user's conversations that they haven't joined. */
export interface IncomingCall {
  callId: string
  /** Who started it — the popup suppresses calls this user started themselves. */
  startedById: string
  startedByName: string
  startedAt: string
  participants: CallParticipantSummary[]
  /**
   * Ring state for every member, straight from the server. This viewer's own
   * entry is what gates the ringtone — see `myRingState`. Persisted server-side,
   * which is what makes "answered on another device" survive a reload here.
   */
  ring: CallRingEntry[]
}

/**
 * This user's own ring state for a call, defaulting to `ringing` when the
 * server didn't send a roster (an older API, or an event that predates it).
 * Defaulting to `ringing` keeps the pre-ring-state behaviour rather than
 * silently swallowing a call.
 */
export function myRingState(call: IncomingCall, userId: string | null): CallRingState {
  if (!userId) return 'ringing'
  return call.ring.find((entry) => entry.user_id === userId)?.state ?? 'ringing'
}

/**
 * Why this device has to give up a call it is on.
 *
 * `handoff` — the user joined it on another device, which now owns the seat.
 * `ended`   — the call is over (someone declined, or it ended remotely).
 *
 * Both mean the same thing mechanically: hang the media up, drop the dock, and
 * do NOT report a leave. They differ only in what the user is told.
 */
export type CallReleaseReason = 'handoff' | 'ended'

export interface CallSlice {
  activeCall: ActiveCallSession | null
  /** Joinable calls, keyed by THIS user's contact_id for the conversation. */
  incomingCalls: Record<string, IncomingCall>

  setActiveCall: (session: ActiveCallSession) => void
  clearActiveCall: () => void
  setCallMinimized: (minimized: boolean) => void
  upsertIncomingCall: (contactId: string, call: IncomingCall) => void
  removeIncomingCall: (contactId: string) => void
  /**
   * Drop a call by its id, wherever it is. Used on `call.ended`.
   *
   * If we are IN that call this does not clear the session — it marks it
   * `releasing` so CallHost can hang the media up first.
   */
  removeCallById: (callId: string, reason?: CallReleaseReason) => void
  /**
   * Optimistically record that THIS user declined, so the popup reacts on the
   * click rather than on the round trip. The server's `call.ring_changed` and
   * the next hydration both converge on the same value.
   */
  declineOwnRing: (contactId: string) => void
  /** Apply a realtime call event (started/ended/roster/ring) to the map. */
  applyCallEvent: (event: CallRealtimeEvent) => void
  /** Reconcile a conversation's call from the REST hydration read. */
  syncIncomingCall: (contactId: string, call: ActiveCall | null) => void
  /**
   * Reconcile the seat we think we hold against a hydration read of the call
   * we're on. The durable backstop for anything the socket missed: the call
   * ended, or another of this user's devices took the seat.
   *
   * `fetchedAt` is the read's timestamp. The query is shared with the open
   * thread, which has usually cached `{call: null}` from before the call
   * existed, so a read older than `joinedAt` says nothing and is ignored.
   */
  reconcileSeat: (call: ActiveCall | null, fetchedAt: number) => void
}

/**
 * Live call state. Ephemeral by design — nothing here is persisted (see
 * `partialize` in ./index): the auth token is a credential, and a call cannot
 * outlive the tab that holds its WebRTC connection.
 *
 * `incomingCalls` is keyed by contact_id rather than call id so the chat header
 * and the thread card can both answer "is there a call in THIS conversation?"
 * with a single lookup.
 */
export const createCallSlice: StateCreator<DashboardStore, PersistMutator, [], CallSlice> = (set, get) => ({
  activeCall: null,
  incomingCalls: {},

  setActiveCall: (session) => set({ activeCall: session }),
  clearActiveCall: () => set({ activeCall: null }),
  setCallMinimized: (minimized) =>
    set((state) => (state.activeCall ? { activeCall: { ...state.activeCall, minimized } } : {})),

  upsertIncomingCall: (contactId, call) =>
    set((state) => ({ incomingCalls: { ...state.incomingCalls, [contactId]: call } })),

  removeIncomingCall: (contactId) =>
    set((state) => {
      if (!(contactId in state.incomingCalls)) return {}
      const next = { ...state.incomingCalls }
      delete next[contactId]
      return { incomingCalls: next }
    }),

  removeCallById: (callId, reason = 'ended') =>
    set((state) => {
      const incomingCalls = Object.fromEntries(
        Object.entries(state.incomingCalls).filter(([, call]) => call.callId !== callId),
      )
      // In it — hand the teardown to CallHost rather than clearing the session
      // here. Dropping the dock on its own orphans the media connection.
      return state.activeCall?.callId === callId
        ? { incomingCalls, activeCall: { ...state.activeCall, releasing: reason } }
        : { incomingCalls }
    }),

  declineOwnRing: (contactId) =>
    set((s) => {
      const userId = s.currentUserId
      const call = s.incomingCalls[contactId]
      if (!userId || !call) return s
      const ring = call.ring.some((entry) => entry.user_id === userId)
        ? call.ring.map((e) => (e.user_id === userId ? { ...e, state: 'declined' as const } : e))
        : [...call.ring, { user_id: userId, name: null, state: 'declined' as const }]
      return { incomingCalls: { ...s.incomingCalls, [contactId]: { ...call, ring } } }
    }),

  applyCallEvent: (event) => {
    const { upsertIncomingCall, removeCallById, incomingCalls } = get()

    if (event.type === 'call.started') {
      upsertIncomingCall(event.contact_id, {
        callId: event.data.call_id,
        startedById: event.data.started_by.id,
        startedByName: event.data.started_by.name ?? 'Someone',
        startedAt: event.data.started_at,
        participants: event.data.participants,
        ring: event.data.ring ?? [],
      })
      return
    }

    if (event.type === 'call.participants_changed') {
      const existing = incomingCalls[event.contact_id]
      if (existing) {
        upsertIncomingCall(event.contact_id, { ...existing, participants: event.data.participants })
      }
      return
    }

    if (event.type === 'call.ring_changed') {
      const existing = incomingCalls[event.contact_id]
      if (existing) {
        upsertIncomingCall(event.contact_id, { ...existing, ring: event.data.ring })
      }
      // Also feeds the dock of whoever is IN the call — that's the caller
      // watching "Ringing…" turn into "Declined".
      set((state) =>
        state.activeCall?.callId === event.data.call_id
          ? { activeCall: { ...state.activeCall, ring: event.data.ring } }
          : {},
      )
      return
    }

    if (event.type === 'call.ring_resolved') {
      // Us, on this very device — we already did whatever this describes.
      if (event.data.device_id && event.data.device_id === getDeviceId()) return

      // Another of this user's devices answered or declined. Stand down: drop
      // the popup, and if WE held the media session, release it. The handoff.
      removeCallById(event.data.call_id, 'handoff')
      return
    }

    // call.ended — drops the pill, and releases the dock if we were in it.
    removeCallById(event.data.call_id)
  },

  syncIncomingCall: (contactId, call) => {
    if (!call) {
      get().removeIncomingCall(contactId)
      return
    }
    get().upsertIncomingCall(contactId, {
      callId: call.id,
      startedById: call.started_by.id,
      startedByName: call.started_by.name ?? 'Someone',
      startedAt: call.started_at,
      participants: call.participants,
      // The durable half of the multi-device fix: a device that was asleep when
      // the call was answered elsewhere learns it from this read, not from an
      // event it was never awake to receive.
      ring: call.ring ?? [],
    })
  },

  reconcileSeat: (call, fetchedAt) => {
    const { activeCall, removeCallById } = get()
    if (!activeCall || activeCall.releasing) return
    // Older than our join — see the doc on `fetchedAt`. Says nothing.
    if (fetchedAt <= activeCall.joinedAt) return

    // The call we're on is gone: it ended while our socket was down.
    if (!call || call.id !== activeCall.callId) {
      removeCallById(activeCall.callId, 'ended')
      return
    }

    // Still live, but a different device of ours holds the seat: we were handed
    // off while our socket was down and missed `call.ring_resolved`. Without
    // this the dock — and the microphone behind it — would stay up forever.
    if (call.viewer_device_id && call.viewer_device_id !== getDeviceId()) {
      removeCallById(activeCall.callId, 'handoff')
    }
  },
})
